/**
 * Pricing — page /pricing
 *
 * 3 états par carte :
 *  - plan actuel     → badge "Plan actuel" + bouton inactif
 *  - plan sélectionné (≠ actuel) → ring highlight + bouton "Sélectionné ✓"
 *  - autre plan       → bouton "Choisir"
 *
 * Section code promo séparée : saisie → appel POST /api/user/promo → mise à jour profil.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Crown, Zap, Tag, Loader2, X, ChevronRight, Lock } from 'lucide-react';
import { useCredits } from './hooks/useCredits';
import { PLAN_CONFIG, ACTION_COSTS } from './lib/credits-store';
import { apiFetch } from './lib/api-fetch';
import { useAuth } from './contexts/AuthContext';
import { useI18n } from './contexts/I18nContext';
import { toast } from 'sonner';

type Plan = 'free' | 'mini' | 'premium';

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
}

export function Pricing() {
  const navigate = useNavigate();
  const { plan: currentPlan, planExpiresAt, refreshFromServer } = useCredits();
  const { user } = useAuth();
  const { t } = useI18n();

  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [promoCode, setPromoCode] = useState('');
  const [promoStatus, setPromoStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [promoMessage, setPromoMessage] = useState('');

  // Le plan "en attente de confirmation" (≠ plan actif)
  const pendingPlan = selectedPlan && selectedPlan !== currentPlan ? selectedPlan : null;

  async function handlePromo(e: React.FormEvent) {
    e.preventDefault();
    if (!promoCode.trim()) return;
    if (!user) {
      toast.error(t('pricing.loginForPromo'));
      return;
    }
    setPromoStatus('loading');
    setPromoMessage('');
    try {
      const { res, json } = await apiFetch<{ plan?: string; plan_expires_at?: string; message?: string; error?: string }>(
        '/api/user/promo',
        { code: promoCode.trim() },
      );
      if (!res.ok) {
        setPromoStatus('error');
        setPromoMessage((json as { error?: string }).error ?? t('pricing.promoInvalid'));
      } else {
        setPromoStatus('success');
        setPromoMessage((json as { message?: string }).message ?? t('pricing.planActivated'));
        setPromoCode('');
        await refreshFromServer();
        toast.success((json as { message?: string }).message ?? t('pricing.planActivated'));
      }
    } catch {
      setPromoStatus('error');
      setPromoMessage(t('pricing.networkError'));
    }
  }

  const FREE_FEATURES = [
    t('pricing.features.ptsPerMonth').replace('{pts}', String(PLAN_CONFIG.free.monthlyPoints)),
    t('pricing.features.versions3'),
    t('pricing.features.offers5'),
    t('pricing.features.exportBasic'),
    t('pricing.features.allTemplates'),
    t('pricing.features.cloudSync'),
    t('pricing.features.earnPts'),
  ];

  const MINI_FEATURES = [
    t('pricing.features.ptsPerMonth').replace('{pts}', String(PLAN_CONFIG.mini.monthlyPoints)),
    t('pricing.features.versions10'),
    t('pricing.features.offers20'),
    t('pricing.features.exportUnlimited'),
    t('pricing.features.allTemplates'),
  ];

  const PREMIUM_FEATURES = [
    t('pricing.features.unlimitedPts'),
    t('pricing.features.unlimitedVersions'),
    t('pricing.features.unlimitedOffers'),
    t('pricing.features.unlimitedExport'),
    t('pricing.features.allTemplates'),
    t('pricing.features.prioritySupport'),
  ];

  const PLANS: { id: Plan; label: string; price: string; sub: string; features: string[]; accent: string; ring: string; badge: string; icon: React.ReactNode }[] = [
    {
      id: 'free',
      label: t('userMenu.planFree'),
      price: '0€',
      sub: t('pricing.foreverSub'),
      features: FREE_FEATURES,
      accent: 'border-white/10 bg-white/[0.02]',
      ring: 'border-blue-500/50 bg-blue-500/5 ring-1 ring-blue-500/30',
      badge: 'bg-blue-500/20 text-blue-300 border-blue-500/20',
      icon: null,
    },
    {
      id: 'mini',
      label: t('userMenu.planMini'),
      price: '4€',
      sub: '/mois',
      features: MINI_FEATURES,
      accent: 'border-sky-500/20 bg-sky-500/[0.03]',
      ring: 'border-sky-500/50 bg-sky-500/[0.07] ring-1 ring-sky-500/40',
      badge: 'bg-sky-500/20 text-sky-300 border-sky-500/20',
      icon: <Zap className="size-4 text-sky-400" />,
    },
    {
      id: 'premium',
      label: t('userMenu.planPremium'),
      price: '9€',
      sub: '/mois',
      features: PREMIUM_FEATURES,
      accent: 'border-amber-500/30 bg-gradient-to-br from-amber-500/8 to-orange-500/3',
      ring: 'border-amber-500/50 bg-gradient-to-br from-amber-500/12 to-orange-500/6 ring-1 ring-amber-500/40',
      badge: 'bg-amber-500/20 text-amber-300 border-amber-500/20',
      icon: <Crown className="size-4 text-amber-400" />,
    },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header */}
      <div className="sticky top-0 z-20 flex items-center gap-3 px-6 py-4 border-b border-white/10 bg-[#0a0a0a]/95 backdrop-blur-sm">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-white/50 hover:text-white text-sm transition-colors"
        >
          <ArrowLeft className="size-4" />
          {t('pricing.back')}
        </button>
        <div className="w-px h-4 bg-white/10" />
        <h1 className="text-base font-bold">{t('pricing.title')}</h1>
        {planExpiresAt && (
          <span className="ml-auto text-xs text-amber-400/80 bg-amber-500/10 border border-amber-500/20 px-2.5 py-1 rounded-full">
            {t('pricing.tempPlan').replace('{date}', formatDate(planExpiresAt))}
          </span>
        )}
      </div>

      {/* Hero */}
      <div className="max-w-4xl mx-auto px-6 pt-14 pb-10 text-center">
        <h2 className="text-3xl md:text-4xl font-black tracking-tight mb-3">
          {t('pricing.chooseYourPlan')}
        </h2>
        <p className="text-white/50 text-base max-w-xl mx-auto">
          {t('pricing.heroSub')}
        </p>
      </div>

      <div className="max-w-4xl mx-auto px-6 pb-24 space-y-10">

        {/* Plan cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {PLANS.map((p) => {
            const isCurrent = p.id === currentPlan;
            const isSelected = p.id === selectedPlan && !isCurrent;
            const cardClass = isCurrent ? p.ring : isSelected ? p.ring : p.accent;

            return (
              <div
                key={p.id}
                className={`rounded-2xl border p-6 space-y-5 transition-all duration-200 ${cardClass} ${!isCurrent ? 'cursor-pointer hover:brightness-110' : ''}`}
                onClick={() => { if (!isCurrent) setSelectedPlan(p.id); }}
              >
                {/* En-tête */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-bold flex items-center gap-2">
                      {p.icon}
                      {p.label}
                    </h3>
                    {isCurrent && (
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${p.badge}`}>
                        {t('pricing.currentPlanBadge')}
                      </span>
                    )}
                    {isSelected && (
                      <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-white/10 text-white/70 border border-white/15">
                        <Check className="size-3" />
                        {t('pricing.selectedBadge')}
                      </span>
                    )}
                  </div>
                  <div className="flex items-end gap-1">
                    <span className="text-3xl font-black">{p.price}</span>
                    <span className="text-sm text-white/40 mb-1">{p.sub}</span>
                  </div>
                </div>

                {/* Features */}
                <ul className="space-y-2">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-white/70">
                      <Check className={`size-4 shrink-0 mt-0.5 ${p.id === 'free' ? 'text-emerald-400' : p.id === 'mini' ? 'text-sky-400' : 'text-amber-400'}`} />
                      {f}
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                {isCurrent ? (
                  <div className={`flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold border ${p.badge} opacity-60`}>
                    {p.id === 'free' ? t('pricing.freePlanActive') : t('pricing.planActive').replace('{plan}', p.label)}
                  </div>
                ) : isSelected ? (
                  <div className="flex items-center justify-center gap-2 py-2.5 rounded-xl border border-white/20 text-white text-sm font-semibold bg-white/5">
                    <Check className="size-4" />
                    {t('pricing.selectedConfirm')}
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setSelectedPlan(p.id); }}
                    className="w-full py-2.5 rounded-xl border border-white/15 text-white/60 hover:text-white hover:bg-white/5 text-sm font-semibold transition-all"
                  >
                    {t('pricing.choosePlan').replace('{plan}', p.label)}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Confirmation section — apparaît quand un plan différent est sélectionné */}
        {pendingPlan && (() => {
          const p = PLANS.find((x) => x.id === pendingPlan)!;
          return (
            <div className="rounded-2xl border border-white/15 bg-white/[0.03] p-6 space-y-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white/50 mb-1">{t('pricing.changePlan')}</p>
                  <h3 className="text-lg font-bold flex items-center gap-2">
                    {p.icon}
                    {t('pricing.switchToPlan').replace('{plan}', p.label)}
                    <span className="text-white/40 font-normal text-base">— {p.price}{p.id !== 'free' ? p.sub : ''}</span>
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedPlan(null)}
                  className="p-1.5 rounded-lg text-white/30 hover:text-white hover:bg-white/10 transition-all"
                  aria-label={t('common.cancel')}
                >
                  <X className="size-4" />
                </button>
              </div>

              {/* Payment (placeholder) */}
              <button
                type="button"
                disabled
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-white/5 border border-white/10 text-white/30 text-sm font-semibold cursor-not-allowed"
              >
                <Lock className="size-4" />
                {t('pricing.paymentCard')}
              </button>

              {/* Divider */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-white/10" />
                <span className="text-xs text-white/30 font-medium">{t('pricing.or')}</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>

              {/* Promo code inline */}
              <div>
                <p className="text-sm text-white/60 mb-3 flex items-center gap-1.5">
                  <Tag className="size-3.5" />
                  {t('pricing.havePromo')}
                </p>
                <form onSubmit={handlePromo} className="flex gap-2">
                  <input
                    type="text"
                    value={promoCode}
                    onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoStatus('idle'); setPromoMessage(''); }}
                    placeholder={t('pricing.promoPlaceholder')}
                    className="flex-1 bg-white/5 border border-white/15 rounded-xl px-4 py-2.5 text-sm font-mono placeholder-white/20 focus:outline-none focus:border-white/30 focus:ring-1 focus:ring-white/20 transition-all uppercase tracking-widest"
                    disabled={promoStatus === 'loading' || promoStatus === 'success'}
                  />
                  <button
                    type="submit"
                    disabled={!promoCode.trim() || promoStatus === 'loading' || promoStatus === 'success'}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white text-black text-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-white/90 transition-all"
                  >
                    {promoStatus === 'loading' ? <Loader2 className="size-4 animate-spin" /> : <ChevronRight className="size-4" />}
                    {t('pricing.activate')}
                  </button>
                </form>

                {promoMessage && (
                  <p className={`mt-2.5 text-sm flex items-start gap-2 ${promoStatus === 'success' ? 'text-emerald-400' : 'text-red-400'}`}>
                    {promoStatus === 'success'
                      ? <Check className="size-4 shrink-0 mt-0.5" />
                      : <X className="size-4 shrink-0 mt-0.5" />}
                    {promoMessage}
                  </p>
                )}
              </div>
            </div>
          );
        })()}

        {/* Code promo standalone (si aucun plan sélectionné) */}
        {!pendingPlan && (
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
            <div className="flex items-center gap-2 mb-4">
              <Tag className="size-4 text-white/40" />
              <h3 className="text-sm font-bold text-white/70">{t('pricing.promoCodeLabel')}</h3>
            </div>
            <form onSubmit={handlePromo} className="flex gap-2">
              <input
                type="text"
                value={promoCode}
                onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoStatus('idle'); setPromoMessage(''); }}
                placeholder={t('pricing.enterPromoPlaceholder')}
                className="flex-1 bg-white/5 border border-white/15 rounded-xl px-4 py-2.5 text-sm font-mono placeholder-white/20 focus:outline-none focus:border-white/30 focus:ring-1 focus:ring-white/20 transition-all uppercase tracking-widest"
                disabled={promoStatus === 'loading' || promoStatus === 'success'}
              />
              <button
                type="submit"
                disabled={!promoCode.trim() || promoStatus === 'loading' || promoStatus === 'success'}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white text-black text-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-white/90 transition-all"
              >
                {promoStatus === 'loading' ? <Loader2 className="size-4 animate-spin" /> : t('pricing.activate')}
              </button>
            </form>
            {promoMessage && (
              <p className={`mt-2.5 text-sm flex items-start gap-2 ${promoStatus === 'success' ? 'text-emerald-400' : 'text-red-400'}`}>
                {promoStatus === 'success'
                  ? <Check className="size-4 shrink-0 mt-0.5" />
                  : <X className="size-4 shrink-0 mt-0.5" />}
                {promoMessage}
              </p>
            )}
          </div>
        )}

        {/* Tableau des coûts */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
          <h3 className="text-sm font-bold text-white/50 mb-4 uppercase tracking-wider">{t('pricing.actionCostsTitle')}</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
            {([
              [t('pricing.actionCostAiAnalysis'), ACTION_COSTS.aiAnalysis],
              [t('pricing.actionCostCompare'), ACTION_COSTS.compare],
              [t('pricing.actionCostOptimize'), ACTION_COSTS.optimize],
              [t('pricing.actionCostCoverLetter'), ACTION_COSTS.coverLetter],
              [t('pricing.actionCostPdf'), ACTION_COSTS.exportPdf],
              [t('pricing.actionCostDocx'), ACTION_COSTS.exportDocx],
            ] as const).map(([label, cost]) => (
              <div key={label} className="flex items-center justify-between p-2.5 rounded-lg bg-white/5">
                <span className="text-xs text-white/55">{label}</span>
                <span className="text-xs font-bold text-white/80 font-mono ml-3 shrink-0">{cost} pt{cost > 1 ? 's' : ''}</span>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-white/25 mt-3">{t('pricing.actionCostsFree')}</p>
        </div>

        {/* FAQ */}
        <div className="space-y-3">
          <h3 className="text-base font-bold text-center text-white/80">{t('pricing.faqTitle')}</h3>
          {([
            { q: t('pricing.faq1q'), a: t('pricing.faq1a') },
            { q: t('pricing.faq2q'), a: t('pricing.faq2a') },
            { q: t('pricing.faq3q'), a: t('pricing.faq3a') },
            { q: t('pricing.faq4q'), a: t('pricing.faq4a') },
          ]).map(({ q, a }) => (
            <details key={q} className="group rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
              <summary className="flex items-center justify-between px-4 py-3 cursor-pointer text-sm font-semibold text-white/70 hover:text-white transition-colors list-none">
                {q}
                <X className="size-3.5 text-white/25 group-open:rotate-45 transition-transform shrink-0 ml-3" />
              </summary>
              <div className="px-4 pb-4 text-sm text-white/45 leading-relaxed">{a}</div>
            </details>
          ))}
        </div>

        <p className="text-center text-xs text-white/20">
          {t('pricing.footerNote')}
        </p>
      </div>
    </div>
  );
}
